// SP-2 WS-7 — plain-sentence narration of the recorded payment's WF1
// allocation. Fired lines only, in position order; skipped/zero lines are a
// muted count (the AllocationDrawer on the Payment Tracking tab remains the
// full audit surface — linked below). Glossary terms (ICUF / arrears / NSF)
// get inline definitions.

import { Link } from "react-router-dom";
import { Card, Skeleton } from "@/design-system/components/ui";
import { useServicingAllocation } from "@/hooks/portfolioAdmin/useServicing";
import type { ServicingAllocationLineDto } from "@/api/portfolioAdmin/servicingApi";
import type { MonthlyCycleServicingPaymentDto } from "@/api/portfolioAdmin/loanStatementsApi";
import { fmtMoney } from "../paymentTracking/servicingFormat";
import { formatCalendarDate } from "@/utils/datetime";
import { GlossaryText } from "./GlossaryHint";

export function WaterfallNarrationCard({
  servicingPayment,
}: {
  servicingPayment: MonthlyCycleServicingPaymentDto;
}) {
  const allocation = useServicingAllocation(servicingPayment.paymentId);

  return (
    <Card data-testid="waterfall-narration-card">
      <h3 className="font-display font-bold text-h3 m-0 mb-space-2">
        Where the money went
      </h3>

      {servicingPayment.isReversed && (
        <p
          className="text-small text-status-error border border-status-error rounded-md bg-status-error/5 px-space-3 py-space-2 m-0 mb-space-3"
          data-testid="narration-reversed-banner"
        >
          This payment was reversed — the allocation below no longer stands.
        </p>
      )}

      {allocation.isLoading && (
        <div data-testid="narration-loading">
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-2/3 mt-space-2" />
        </div>
      )}

      {allocation.isError && (
        <p className="text-small text-gray-mid m-0">
          The allocation detail couldn&apos;t be loaded — open the full
          allocation on the Payment Tracking tab.
        </p>
      )}

      {allocation.data && (
        <NarrationBody
          received={allocation.data.amount}
          receivedOn={formatCalendarDate(allocation.data.paymentDate)}
          lines={allocation.data.lines}
        />
      )}

      <p className="text-small m-0 mt-space-3">
        <Link
          to={`?tab=payment-tracking&payment=${servicingPayment.paymentId}`}
          className="text-gold-shadow hover:underline"
          data-testid="narration-full-allocation-link"
        >
          View full allocation
        </Link>
      </p>
    </Card>
  );
}

function NarrationBody({
  received,
  receivedOn,
  lines,
}: {
  received: number;
  receivedOn: string;
  lines: ServicingAllocationLineDto[];
}) {
  const fired = [...lines]
    .filter((l) => l.status === "fired")
    .sort((a, b) => a.position - b.position);
  const quietCount = lines.length - fired.length;

  return (
    <div className="flex flex-col gap-space-2">
      <p className="text-body text-onyx m-0">
        {fmtMoney(received)} received on {receivedOn} — here&apos;s how the
        waterfall split it:
      </p>
      {fired.length === 0 ? (
        <p className="text-small text-gray-mid m-0">
          No waterfall line took money from this payment.
        </p>
      ) : (
        <ul className="list-none p-0 m-0 flex flex-col gap-space-1">
          {fired.map((line) => (
            <li
              key={line.position}
              className="text-small text-onyx"
              data-testid={`narration-line-${line.position}`}
            >
              {fmtMoney(line.amount)} →{" "}
              <GlossaryText
                text={line.obligationTypeDisplay ?? line.obligationTypeCode}
              />
              {line.recipientName ? ` (${line.recipientName})` : ""}
            </li>
          ))}
        </ul>
      )}
      {quietCount > 0 && (
        <p className="text-caption text-gray-mid m-0" data-testid="narration-skipped-note">
          {quietCount} other waterfall {quietCount === 1 ? "line" : "lines"} had
          nothing to pay this period.
        </p>
      )}
    </div>
  );
}
